import { z } from "zod";

export const expertApplicationSchema = z.object({
  // Personal Information
  fullName: z.string().min(2, "Full name is required"),
  email: z.string().email("Valid email is required"),
  phone: z.string().min(1, "Phone number is required for contact and verification"),
  profileImage: z.string().min(1, "Profile image is required to help families connect with you"),
  
  // Professional Details
  professionalTitle: z.string().min(2, "Professional title is required"),
  yearsExperience: z.number().min(1, "Years of experience is required"),
  
  // Pricing Packages - Updated to include pricing for each package
  offeredPackages: z.array(z.string()).min(1, "Please select at least one package you want to offer"),
  packagePricing: z.record(z.string(), z.number().min(10, "Price must be at least €10")).optional(),
  
  // Bio and Background
  bio: z.string().min(100, "Bio must be at least 100 characters"),
  
  // Specializations
  specializations: z.array(z.string()).min(1, "At least one specialization is required"),
  otherSpecialization: z.string().optional(),
  
  // Availability
  availability: z.array(z.string()).min(1, "Please select your availability"),
  
  // Professional Links
  linkedinUrl: z.string().url("Please enter a valid LinkedIn URL").optional().or(z.literal("")),
  websiteUrl: z.string().url("Please enter a valid website URL").optional().or(z.literal("")),
  
  // Additional Professional Information
  certifications: z.array(z.string()).min(1, "At least one professional certification is required"),
  cvFile: z.string().optional(),
  
  // Agreement
  agreeToTerms: z.boolean().refine(val => val === true, "You must agree to the terms"),
});

export type ExpertApplicationData = z.infer<typeof expertApplicationSchema>;

export const availableSpecializations = [
  { 
    id: "fair-deal", 
    label: "Fair Deal Applications", 
    tooltip: "Help families navigate the Fair Deal Scheme application process and financial assessments" 
  },
  { 
    id: "financial-planning", 
    label: "Financial Planning", 
    tooltip: "Assist with care cost planning, asset protection, and financial strategies" 
  },
  { 
    id: "care-assessment", 
    label: "Care Needs Assessment", 
    tooltip: "Evaluate individual care requirements and recommend appropriate levels of care" 
  },
  { 
    id: "medical-planning", 
    label: "Medical Care Planning", 
    tooltip: "Coordinate medical care transitions and specialized healthcare needs" 
  },
  { 
    id: "quality-evaluation", 
    label: "Quality Evaluation", 
    tooltip: "Assess nursing home quality, standards, and suitability for specific needs" 
  },
  { 
    id: "family-counseling", 
    label: "Family Counseling", 
    tooltip: "Support families through difficult decisions and emotional transitions" 
  },
  { 
    id: "transition-support", 
    label: "Transition Support", 
    tooltip: "Guide families through the moving process and settling into care" 
  },
  { 
    id: "emergency-placement", 
    label: "Emergency Placement", 
    tooltip: "Families needing same-day or urgent placements due to hospital discharge or crisis" 
  },
  { 
    id: "crisis-management", 
    label: "Crisis Management", 
    tooltip: "Handle urgent situations requiring immediate care solutions" 
  },
  { 
    id: "complex-cases", 
    label: "Complex Cases", 
    tooltip: "Specialized support for unique or challenging care situations" 
  }
];

export const availabilityOptions = [
  { id: "weekdays", label: "Weekdays" },
  { id: "weekends", label: "Weekends" },
  { id: "evenings", label: "Evenings" },
  { id: "flexible", label: "Flexible schedule" }
];

export const packageOptions = [
  {
    id: "introductory",
    name: "Introductory Consultation",
    suggestedPrice: "€60–€90",
    duration: "30-45 minute video or phone call",
    includes: [
      "Assess family needs",
      "Answer Fair Deal or care planning questions",
      "Recommend next steps"
    ],
    idealFor: "First-time callers needing clarity or quick advice",
    requiredSpecializations: [], // All experts can offer this
    recommendedCertifications: [] // No specific requirements
  },
  {
    id: "standard",
    name: "Standard Support Package",
    suggestedPrice: "€150–€250",
    duration: "Comprehensive consultation and support",
    includes: [
      "1-hour deep dive consultation",
      "Review of relevant documentation (e.g., assets, care reports)",
      "Written summary or action plan",
      "Follow-up email support (1-2 emails)"
    ],
    idealFor: "Families actively preparing for care home entry or Fair Deal applications",
    requiredSpecializations: [], // All experts can offer this
    recommendedCertifications: ["social-work", "nursing", "financial-planning", "legal"]
  },
  {
    id: "premium",
    name: "Premium Case Management",
    suggestedPrice: "€300–€500+",
    duration: "Full guidance across a specific care placement or transition",
    includes: [
      "Up to 3 consultation hours",
      "Assistance completing applications",
      "Liaising with nursing homes or health professionals",
      "Availability during urgent decision windows"
    ],
    idealFor: "Emergency placements, out-of-country families, complex cases",
    requiredSpecializations: ["emergency-placement", "crisis-management", "complex-cases"], // At least one of these
    recommendedCertifications: ["social-work", "nursing", "legal", "healthcare-management"]
  }
];

export const certificationOptions = [
  { id: "social-work", label: "Social Work Degree/Qualification" },
  { id: "nursing", label: "Nursing Qualification" },
  { id: "counseling", label: "Counseling/Psychology Qualification" },
  { id: "elder-care", label: "Elder Care Specialist" },
  { id: "financial-planning", label: "Financial Planning Certification" },
  { id: "legal", label: "Legal Qualification" },
  { id: "healthcare-management", label: "Healthcare Management" },
  { id: "other", label: "Other Professional Qualification" }
];
